// src/features/workspace/useUserNames.ts
// Resolves user profile ids to display names for filter chips and menus.
// Cached for the session: the same people appear in every filter.

import { useEffect, useState } from 'react';

import { userprofileApi } from '@core/api';
import { candidateFromProfile } from '@/features/userprofile/pickerCandidates';

const cache = new Map<string, string>();
const pending = new Set<string>();

export function useUserNames(ids: string[]): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>(() =>
    Object.fromEntries([...cache.entries()]),
  );

  const missingKey = ids
    .filter((id) => id && !cache.has(id) && !pending.has(id))
    .sort()
    .join(',');

  useEffect(() => {
    if (!missingKey) return;
    let cancelled = false;
    const idsToFetch = missingKey.split(',');
    idsToFetch.forEach((id) => pending.add(id));
    userprofileApi
      .ListUserprofilesByIDs({ ids: idsToFetch })
      .then((res: any) => {
        const profiles = res?.userprofiles?.profiles || [];
        for (const profile of profiles) {
          const id = profile?.entity?.id;
          const name = id ? candidateFromProfile(profile).displayName : '';
          if (id && name) cache.set(id, name);
        }
      })
      .catch(() => { /* chips fall back to the short id */ })
      .finally(() => {
        idsToFetch.forEach((id) => pending.delete(id));
        if (!cancelled) setNames(Object.fromEntries([...cache.entries()]));
      });
    return () => { cancelled = true; };
  }, [missingKey]);

  return names;
}
